import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, RefreshCcw, ScrollText, ChevronDown, ChevronRight, Search,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

function fmtAgo(iso) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.round(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Pretty colour per action prefix so the table is scannable
const ACTION_TONES = {
  'user.': 'bg-blue-100 text-blue-800 border-blue-200',
  'coach.': 'bg-purple-100 text-purple-800 border-purple-200',
  'landing.': 'bg-amber-100 text-amber-800 border-amber-200',
  'llm_budget.': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'ops.': 'bg-gray-100 text-gray-700 border-gray-200',
};
function actionTone(action) {
  for (const prefix of Object.keys(ACTION_TONES)) {
    if (action?.startsWith(prefix)) return ACTION_TONES[prefix];
  }
  return 'bg-gray-100 text-gray-700 border-gray-200';
}

export default function AuditLogPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(0);
  const [actions, setActions] = useState([]);
  const [filters, setFilters] = useState({ action: '', actor_email: '', target_type: '' });
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      if (filters.action) params.set('action', filters.action);
      if (filters.actor_email) params.set('actor_email', filters.actor_email);
      if (filters.target_type) params.set('target_type', filters.target_type);
      const res = await axios.get(`${API}/super-admin/audit-log?${params.toString()}`, { headers });
      setData(res.data);
    } catch (e) {
      // toast handled silently — page shows empty state on auth failures
    } finally {
      setLoading(false);
    }
  }, [page, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/super-admin/audit-log/actions`, { headers })
      .then((r) => setActions(r.data?.actions || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-gray-50" data-testid="audit-log-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/super-admin')}
              data-testid="audit-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-gray-500" />
                Super Admin Audit Log
              </h1>
              <p className="text-xs text-gray-500">
                Append-only record of every super-admin action — {data.total.toLocaleString()} entries
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="audit-refresh-btn"
          >
            <RefreshCcw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        {/* Filters */}
        <Card>
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wide">Action</label>
              <Select
                value={filters.action || 'all'}
                onValueChange={(v) => { setPage(0); setFilters((f) => ({ ...f, action: v === 'all' ? '' : v })); }}
              >
                <SelectTrigger data-testid="audit-filter-action" className="mt-1">
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  {actions.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wide">Actor email contains</label>
              <div className="relative mt-1">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  className="pl-8"
                  value={filters.actor_email}
                  onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, actor_email: e.target.value })); }}
                  placeholder="someone@…"
                  data-testid="audit-filter-actor"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wide">Target type</label>
              <Select
                value={filters.target_type || 'all'}
                onValueChange={(v) => { setPage(0); setFilters((f) => ({ ...f, target_type: v === 'all' ? '' : v })); }}
              >
                <SelectTrigger data-testid="audit-filter-target-type" className="mt-1">
                  <SelectValue placeholder="All targets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All target types</SelectItem>
                  <SelectItem value="user">user</SelectItem>
                  <SelectItem value="landing_cms">landing_cms</SelectItem>
                  <SelectItem value="setting">setting</SelectItem>
                  <SelectItem value="ops">ops</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Entries */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="w-8 px-3 py-2"></th>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">Actor</th>
                    <th className="text-left px-3 py-2">Action</th>
                    <th className="text-left px-3 py-2">Target</th>
                    <th className="text-left px-3 py-2">IP</th>
                  </tr>
                </thead>
                <tbody data-testid="audit-log-rows">
                  {data.items.length === 0 && !loading && (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-gray-400">
                        No entries match these filters.
                      </td>
                    </tr>
                  )}
                  {data.items.map((row) => {
                    const isOpen = expanded === row.id;
                    return (
                      <tr key={row.id} className={`border-b last:border-0 ${isOpen ? 'bg-gray-50' : ''}`}>
                        <td className="px-3 py-2 align-top">
                          <button
                            onClick={() => setExpanded(isOpen ? null : row.id)}
                            className="text-gray-400 hover:text-gray-700"
                            data-testid={`audit-row-toggle-${row.id}`}
                          >
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-3 py-2 align-top text-gray-600 whitespace-nowrap" title={row.created_at}>
                          {fmtAgo(row.created_at)}
                        </td>
                        <td className="px-3 py-2 align-top text-gray-800 whitespace-nowrap">
                          {row.actor_email || '—'}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <Badge variant="outline" className={actionTone(row.action)}>{row.action}</Badge>
                        </td>
                        <td className="px-3 py-2 align-top text-gray-700">
                          <div className="font-mono text-xs truncate max-w-xs" title={row.target_id}>
                            {row.target_label || row.target_id || '—'}
                          </div>
                          {row.target_type && (
                            <div className="text-[10px] uppercase text-gray-400">{row.target_type}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top text-gray-500 font-mono text-xs">
                          {row.ip_address || '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {data.items.flatMap((row) => {
                    if (expanded !== row.id) return [];
                    return [(
                      <tr key={`${row.id}-detail`} className="border-b last:border-0 bg-gray-50">
                        <td colSpan={6} className="px-6 py-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                            <div>
                              <p className="text-gray-500 uppercase tracking-wide mb-1">Before</p>
                              <pre className="bg-white border rounded p-2 overflow-x-auto">{JSON.stringify(row.before, null, 2) || '—'}</pre>
                            </div>
                            <div>
                              <p className="text-gray-500 uppercase tracking-wide mb-1">After</p>
                              <pre className="bg-white border rounded p-2 overflow-x-auto">{JSON.stringify(row.after, null, 2) || '—'}</pre>
                            </div>
                            <div className="md:col-span-2">
                              <p className="text-gray-500 uppercase tracking-wide mb-1">Metadata</p>
                              <pre className="bg-white border rounded p-2 overflow-x-auto">{JSON.stringify(row.metadata, null, 2) || '—'}</pre>
                            </div>
                            <div className="md:col-span-2 text-gray-500">
                              <span className="mr-3">id: <code className="font-mono">{row.id}</code></span>
                              {row.user_agent && <span className="break-all">UA: {row.user_agent}</span>}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )];
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        {data.total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">
              Page {page + 1} of {totalPages} · showing {data.items.length} of {data.total} entries
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                data-testid="audit-page-prev"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                data-testid="audit-page-next"
              >
                Next
              </Button>
            </div>
          </div>
        )}

        <p className="text-xs text-gray-400 text-center pt-2">
          Signed in as {user?.email} · Append-only log · Super admin only
        </p>
      </div>
    </div>
  );
}
